import { format, isValid, parse } from 'date-fns'
import { es } from 'date-fns/locale'
import { CalendarIcon, ChevronDown } from 'lucide-react'
import { useMemo } from 'react'
import { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface DateFilterProps {
  dateFrom: string | null | undefined
  dateTo: string | null | undefined
  onDateRangeChange: (_dateFrom: string | null, _dateTo: string | null) => void
  hideDateTypeSelect?: boolean
  selectedDateType?: string
  buttonClassName?: string
  onlySpecific?: boolean
  minDate?: Date
}

// Date format for URL (YYYY-MM-DD)
const URL_DATE_FORMAT = 'yyyy-MM-dd'

// Helper to parse date from URL format
const parseDateFromUrl = (
  dateString: string | null | undefined
): Date | undefined => {
  if (!dateString) return undefined
  try {
    const date = parse(dateString, URL_DATE_FORMAT, new Date())
    return isValid(date) ? date : undefined
  } catch {
    return undefined
  }
}

// Helper to format date for URL
const formatDateForUrl = (date: Date | undefined): string | null => {
  if (!date || !isValid(date)) return null
  return format(date, URL_DATE_FORMAT)
}

export const DateFilter = ({
  dateFrom,
  dateTo,
  onDateRangeChange,
  buttonClassName,
  onlySpecific = false,
  minDate,
}: DateFilterProps) => {
  // Convert URL date strings to Date objects for the calendar
  const dateRange = useMemo<DateRange>(() => {
    return {
      from: parseDateFromUrl(dateFrom),
      to: parseDateFromUrl(dateTo),
    }
  }, [dateFrom, dateTo])

  // Handle date range selection (for Range tab)
  const handleDateRangeSelect = (range: DateRange | undefined) => {
    if (!range) {
      onDateRangeChange(null, null)
      return
    }

    const fromUrl = formatDateForUrl(range.from)
    const toUrl = formatDateForUrl(range.to)
    onDateRangeChange(fromUrl, toUrl)
  }

  // Handle "From" date selection (for Desde tab)
  const handleFromDateSelect = (date: Date | undefined) => {
    const fromUrl = formatDateForUrl(date)
    onDateRangeChange(fromUrl, null)
  }

  // Handle "To" date selection (for Hasta tab)
  const handleToDateSelect = (date: Date | undefined) => {
    const toUrl = formatDateForUrl(date)
    onDateRangeChange(null, toUrl)
  }

  const handleClear = () => {
    onDateRangeChange(null, null)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Seleccionar rango de fechas"
          className={
            buttonClassName
              ? `h-9 ${buttonClassName} justify-between gap-1.5`
              : 'h-9 gap-1.5'
          }
          variant="outline"
        >
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {dateRange.from && dateRange.to ? (
                // Both dates selected - show range
                <>
                  {format(dateRange.from, 'd MMM', { locale: es })} -{' '}
                  {format(dateRange.to, 'd MMM', { locale: es })}
                </>
              ) : dateRange.from ? (
                // Only from date selected
                <>
                  Desde {format(dateRange.from, 'd MMM yyyy', { locale: es })}
                </>
              ) : dateRange.to ? (
                // Only to date selected
                <>Hasta {format(dateRange.to, 'd MMM yyyy', { locale: es })}</>
              ) : (
                // No dates selected
                'Fecha'
              )}
            </span>
          </div>
          {buttonClassName && <ChevronDown className="h-4 w-4 opacity-50" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Tabs
          defaultValue={
            onlySpecific
              ? 'from'
              : !!dateRange.from && !!dateRange.to
                ? 'range'
                : dateRange.from
                  ? 'from'
                  : dateRange.to
                    ? 'to'
                    : 'from'
          }
        >
          <div className="flex items-center justify-between px-4 pt-3">
            <TabsList
              className={`grid w-full ${onlySpecific ? 'grid-cols-1' : 'grid-cols-3'}`}
            >
              <TabsTrigger
                className="text-sm"
                onClick={handleClear}
                value="from"
              >
                {onlySpecific ? 'Fecha' : 'Desde'}
              </TabsTrigger>
              {!onlySpecific && (
                <>
                  <TabsTrigger
                    className="text-sm"
                    onClick={handleClear}
                    value="to"
                  >
                    Hasta
                  </TabsTrigger>
                  <TabsTrigger
                    className="text-sm"
                    onClick={handleClear}
                    value="range"
                  >
                    Rango
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </div>

          {/* From Date Tab */}
          <TabsContent className="space-y-4 p-4" value="from">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {onlySpecific
                  ? 'Selecciona la fecha'
                  : 'Selecciona la fecha de inicio'}
              </p>
            </div>
            <Calendar
              defaultMonth={dateRange.from || new Date()}
              disabled={minDate ? (date) => date < minDate : undefined}
              mode="single"
              onSelect={handleFromDateSelect}
              selected={dateRange.from}
            />
          </TabsContent>

          {/* To Date Tab - only if not onlySpecific */}
          {!onlySpecific && (
            <TabsContent className="space-y-4 p-4" value="to">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Selecciona la fecha de fin
                </p>
              </div>
              <Calendar
                defaultMonth={dateRange.to || dateRange.from || new Date()}
                disabled={minDate ? (date) => date < minDate : undefined}
                mode="single"
                onSelect={handleToDateSelect}
                selected={dateRange.to}
              />
            </TabsContent>
          )}

          {/* Range Tab - only if not onlySpecific */}
          {!onlySpecific && (
            <TabsContent className="p-0" value="range">
              <Calendar
                defaultMonth={dateRange.from || new Date()}
                disabled={minDate ? (date) => date < minDate : undefined}
                initialFocus
                mode="range"
                numberOfMonths={2}
                onSelect={handleDateRangeSelect}
                selected={dateRange}
              />
            </TabsContent>
          )}
          <div className="flex items-center justify-between px-3 pb-3">
            <Button onClick={handleClear} size="sm" variant="ghost">
              Limpiar todo
            </Button>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}
